import React, { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import { Mail, Lock, Eye, EyeOff, ChevronDown, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import { EnvironmentService, ENVIRONMENTS, Environment } from '../../services/EnvironmentService';
import { cn } from '../../lib/utils';
import { buildLabel } from '../../version';

const GoogleIcon = () => (
  <svg className="size-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
    />
  </svg>
);

export const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [environment, setEnvironmentState] = useState<Environment>(
    EnvironmentService.getCurrentEnvironment()
  );
  const login = useLogin();
  const notify = useNotify();

  const handleEnvironmentChange = (env: Environment) => {
    EnvironmentService.setEnvironment(env.name);
    setEnvironmentState(env);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login({ username, password });
    } catch (error: any) {
      notify(error?.message || 'Invalid credentials. Please try again.', {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const notImplemented = () =>
    notify('Coming soon.', { type: 'info' });

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-surface-2 p-6">
      <div className="w-full max-w-[440px] rounded-card bg-surface p-8 shadow-latio-md sm:p-10">
        {/* Wordmark */}
        <div className="flex items-center justify-center gap-2">
          <img
            src="/assets/latio-logo.png"
            alt="Latio"
            className="size-8 shrink-0 rounded-latio-sm"
          />
          <span className="flex items-baseline gap-1.5">
            <span
              className="text-[26px] font-bold leading-none text-ink"
              style={{ letterSpacing: '-0.02em' }}
            >
              Lat<span className="text-indigo">i</span>o
            </span>
            <span className="text-[26px] font-bold leading-none text-ink-3">Admin</span>
          </span>
        </div>

        {/* Heading */}
        <div className="mt-8 text-center">
          <h1
            className="text-2xl font-bold text-ink"
            style={{ letterSpacing: '-0.02em' }}
          >
            Welcome back
          </h1>
          <p className="mt-1.5 text-sm text-ink-2">Sign in to the admin dashboard.</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
          {/* Environment */}
          <div className="flex flex-col gap-2">
            <Label className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2">
              Environment
            </Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-12 w-full items-center justify-between rounded-input border border-hairline bg-surface px-3.5 text-left transition-colors hover:border-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo focus-visible:ring-offset-2"
                >
                  <span className="flex items-center gap-2.5">
                    <span className="size-2 shrink-0 rounded-full bg-success" />
                    <span className="text-sm font-bold text-ink">{environment.label}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-sm text-ink-3">
                      {environment.apiUrl.replace(/^https?:\/\//, '').replace(/\/api\/v1$/, '')}
                    </span>
                    <ChevronDown className="size-4 text-ink-3" />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[var(--radix-dropdown-menu-trigger-width)]"
              >
                {ENVIRONMENTS.map((env) => (
                  <DropdownMenuItem
                    key={env.name}
                    onSelect={() => handleEnvironmentChange(env)}
                    className="flex items-center justify-between gap-3 py-2.5"
                  >
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-ink">{env.label}</span>
                      <span className="text-xs text-ink-3">
                        {env.apiUrl.replace(/^https?:\/\//, '').replace(/\/api\/v1$/, '')}
                      </span>
                    </span>
                    {env.name === environment.name && (
                      <Check className="size-4 shrink-0 text-indigo" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Email */}
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="username"
              className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2"
            >
              Email
            </Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-ink-3" />
              <Input
                id="username"
                type="email"
                placeholder="you@latio.co"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                className="h-12 rounded-input border-hairline pl-11 text-sm placeholder:text-ink-3 focus-visible:ring-indigo"
              />
            </div>
          </div>

          {/* Password */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label
                htmlFor="password"
                className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2"
              >
                Password
              </Label>
              <button
                type="button"
                onClick={notImplemented}
                className="text-sm font-semibold text-indigo hover:text-indigo-press"
              >
                Forgot?
              </button>
            </div>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-ink-3" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="h-12 rounded-input border-hairline pl-11 pr-11 text-sm placeholder:text-ink-3 focus-visible:ring-indigo"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-2"
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </div>

          {/* Sign in */}
          <Button
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-pill bg-black text-base font-bold text-white hover:bg-black/90"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Signing in...
              </span>
            ) : (
              'Sign in'
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="my-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-hairline" />
          <span className="text-sm text-ink-3">or</span>
          <span className="h-px flex-1 bg-hairline" />
        </div>

        {/* Google */}
        <Button
          type="button"
          variant="outline"
          onClick={notImplemented}
          className={cn(
            'h-12 w-full rounded-pill border-hairline bg-surface text-base font-bold text-ink',
            'hover:bg-surface-2'
          )}
        >
          <GoogleIcon />
          Continue with Google
        </Button>
      </div>

      {/* Readable before signing in, which is the point: when QA looks wrong,
          the first question is which build is serving it. */}
      <p className="mt-6 text-xs text-ink-3" title={`${buildLabel} · built for the ${
        EnvironmentService.getCurrentEnvironment().name
      } environment`}>
        {buildLabel}
      </p>
    </div>
  );
};
